import React, { useState, useEffect, useRef } from 'react';
import { Check, Plus, Trash2, Edit2, Save, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Default tag structure to use when we need a safe fallback
const DEFAULT_TAG = {
  code: '',
  labels: { en: '', cs: '' },
  enabled: true
};

// Ensure a tag has all required properties
const sanitizeTag = (tag) => {
  if (!tag) return null;
  
  return {
    ...DEFAULT_TAG,
    ...tag,
    // Ensure labels exists and has expected structure
    labels: {
      en: tag.labels?.en || tag.label || '',
      cs: tag.labels?.cs || tag.label || ''
    }
  };
};

const FileTagsManagement = ({ tags = [], onChange }) => {
  const { t, i18n } = useTranslation();
  const [editingIndex, setEditingIndex] = useState(null);
  const [newTag, setNewTag] = useState({ ...DEFAULT_TAG });
  const [editedTag, setEditedTag] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [editLanguage, setEditLanguage] = useState('en');
  const inputRefs = useRef({});
  
  // Safety: Process input tags to ensure they are valid
  const normalizedTags = React.useMemo(() => {
    // Guard against null/undefined tags array
    if (!tags || !Array.isArray(tags)) return [];
    
    try {
      // Filter out null items and sanitize each tag
      return tags
        .filter(tag => tag != null)
        .map(tag => sanitizeTag(tag))
        .filter(tag => tag != null);
    } catch (err) {
      console.error("Error normalizing tags:", err);
      return [];
    }
  }, [tags]);

  // When editing starts, copy the tag to a separate state
  useEffect(() => {
    if (editingIndex === null || !normalizedTags || normalizedTags.length === 0) {
      setEditedTag(null);
      return;
    }
    
    try {
      // Safety check for valid index
      if (editingIndex < 0 || editingIndex >= normalizedTags.length) {
        console.warn("Invalid editing index:", editingIndex);
        setEditingIndex(null);
        return;
      }
      
      // Get the tag and make a deep copy
      const tagToEdit = normalizedTags[editingIndex];
      if (!tagToEdit) {
        console.warn("No tag found at index:", editingIndex);
        setEditingIndex(null);
        return;
      }
      
      // Create a safe copy to avoid reference issues
      const safeCopy = sanitizeTag(JSON.parse(JSON.stringify(tagToEdit)));
      setEditedTag(safeCopy);
      
      // Focus the code field by default
      setTimeout(() => {
        if (inputRefs.current['code-' + editingIndex]) {
          inputRefs.current['code-' + editingIndex].focus();
        }
      }, 10);
    } catch (err) {
      console.error("Error preparing tag for edit:", err);
      setEditedTag({ ...DEFAULT_TAG });
    }
  }, [editingIndex, normalizedTags]);

  // When language changes, focus the appropriate input
  useEffect(() => {
    if (editingIndex !== null && inputRefs.current[`${editLanguage}-${editingIndex}`]) {
      setTimeout(() => {
        if (inputRefs.current[`${editLanguage}-${editingIndex}`]) {
          inputRefs.current[`${editLanguage}-${editingIndex}`].focus();
        }
      }, 10);
    }
  }, [editLanguage, editingIndex]);

  const handleToggleTag = (index) => {
    try {
      // Safety check
      if (!normalizedTags || index < 0 || index >= normalizedTags.length) {
        console.warn("Invalid tag index for toggle:", index);
        return;
      }
      
      const updatedTags = [...normalizedTags];
      updatedTags[index] = {
        ...updatedTags[index],
        enabled: !updatedTags[index].enabled
      };
      
      onChange(updatedTags);
    } catch (err) {
      console.error("Error toggling tag:", err);
    }
  };

  const handleEditTag = (index) => {
    try {
      // Safety check
      if (!normalizedTags || index < 0 || index >= normalizedTags.length) {
        console.warn("Invalid tag index for edit:", index);
        return;
      }
      
      setEditingIndex(index);
      setEditLanguage('en'); // Default to English when starting edit
    } catch (err) {
      console.error("Error starting tag edit:", err);
    }
  };

  const handleSaveEdit = (index) => {
    try {
      // Safety checks
      if (!editedTag) {
        console.warn("No edited tag to save");
        return;
      }
      
      if (index < 0 || index >= normalizedTags.length) {
        console.warn("Invalid index for saving edited tag:", index);
        setEditingIndex(null);
        return;
      }

      // Validate
      const newErrors = {};
      if (!editedTag.code) {
        newErrors[`code-${index}`] = 'Code is required';
      }
      
      // Check if at least English label is provided
      if (!editedTag.labels?.en) {
        newErrors[`label-en-${index}`] = 'English label is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        
        // Focus the first field with an error
        setTimeout(() => {
          if (newErrors[`code-${index}`] && inputRefs.current[`code-${index}`]) {
            inputRefs.current[`code-${index}`].focus();
          } else if (newErrors[`label-en-${index}`] && inputRefs.current[`en-${index}`]) {
            setEditLanguage('en');
            inputRefs.current[`en-${index}`].focus();
          }
        }, 10);
        
        return;
      }

      // Apply the edited tag back to the normalized tags
      const updatedTags = [...normalizedTags];
      updatedTags[index] = sanitizeTag(editedTag);
      
      onChange(updatedTags);
      setEditingIndex(null);
      setErrors({});
    } catch (err) {
      console.error("Error saving edited tag:", err);
      setEditingIndex(null);
    }
  };

  const handleDeleteTag = (index) => {
    try {
      // Safety check
      if (!normalizedTags || index < 0 || index >= normalizedTags.length) {
        console.warn("Invalid tag index for delete:", index);
        return;
      }
      
      const tagToDelete = normalizedTags[index];
      
      // Prevent deletion of 'all' tag
      if (tagToDelete.code === 'all') {
        alert(t('fileTags.cannotDelete'));
        return;
      }
      
      if (window.confirm(t('common.messages.confirmDelete'))) {
        const updatedTags = [...normalizedTags];
        updatedTags.splice(index, 1);
        onChange(updatedTags);
      }
    } catch (err) {
      console.error("Error deleting tag:", err);
    }
  };

  const handleChange = (index, field, value, language = null) => {
    try {
      if (!editedTag) {
        console.warn("No edited tag to change");
        return;
      }
      
      const updatedTag = { ...editedTag };
      
      if (language) {
        // Update a specific language label
        updatedTag.labels = {
          ...updatedTag.labels,
          [language]: value
        };
      } else {
        // Update other fields
        updatedTag[field] = value;
      }
      
      setEditedTag(updatedTag);
      
      // Keep focus on the input field that's being edited
      setTimeout(() => {
        if (language) {
          if (inputRefs.current[`${language}-${index}`]) {
            inputRefs.current[`${language}-${index}`].focus();
          }
        } else if (field === 'code') {
          if (inputRefs.current[`code-${index}`]) {
            inputRefs.current[`code-${index}`].focus();
          }
        }
      }, 0);
      
      // Clear error for this field if it exists
      if (language) {
        if (errors[`label-${language}-${index}`]) {
          const newErrors = { ...errors };
          delete newErrors[`label-${language}-${index}`];
          setErrors(newErrors);
        }
      } else if (errors[`${field}-${index}`]) {
        const newErrors = { ...errors };
        delete newErrors[`${field}-${index}`];
        setErrors(newErrors);
      }
    } catch (err) {
      console.error("Error updating tag field:", err);
    }
  };

  const handleNewTagChange = (field, value, language = null) => {
    try {
      if (language) {
        setNewTag({
          ...newTag,
          labels: {
            ...newTag.labels,
            [language]: value
          }
        });
        
        // Clear error for this field if it exists
        if (errors[`new-label-${language}`]) {
          const newErrors = { ...errors };
          delete newErrors[`new-label-${language}`];
          setErrors(newErrors);
        }
      } else {
        setNewTag({ ...newTag, [field]: value });
        
        // Clear error for this field if it exists
        if (errors[`new-${field}`]) {
          const newErrors = { ...errors };
          delete newErrors[`new-${field}`];
          setErrors(newErrors);
        }
      }
    } catch (err) {
      console.error("Error updating new tag field:", err);
    }
  };

  const handleAddTag = () => {
    try {
      // Validate
      const newErrors = {};
      if (!newTag.code) newErrors['new-code'] = t('common.validation.required');
      if (!newTag.labels.en) newErrors['new-label-en'] = t('common.validation.required');
      if (!newTag.labels.cs) newErrors['new-label-cs'] = t('common.validation.required');

      if (Object.keys(newErrors).length > 0) {
        setErrors({ ...errors, ...newErrors });
        // Switch to the tab with error if needed
        if (newErrors['new-label-cs'] && !newErrors['new-label-en']) {
          setEditLanguage('cs');
        }
        return;
      }
      
      // Check for duplicate code
      if (normalizedTags.some(tag => tag.code === newTag.code)) {
        setErrors({ ...errors, 'new-code': 'Tag code must be unique' });
        return;
      }

      onChange([...normalizedTags, sanitizeTag(newTag)]);
      setNewTag({ ...DEFAULT_TAG });
      setIsAdding(false);
      setErrors({});
    } catch (err) {
      console.error("Error adding new tag:", err);
    }
  };

  // Get the appropriate label based on current language
  const getDisplayLabel = (tag) => {
    if (!tag) return '';
    
    try {
      const currentLanguage = i18n.language;
      
      // Check if we have multilingual labels
      if (tag.labels && tag.labels[currentLanguage]) {
        return tag.labels[currentLanguage];
      }
      
      // Fallback to English or whatever label is available
      if (tag.labels && tag.labels.en) {
        return tag.labels.en;
      }
      
      // Fallback to old format or just code
      return tag.label || tag.code || '';
    } catch (err) {
      console.error("Error getting display label:", err);
      return tag.code || '';
    }
  };

  // Always ensure we have at least the "all" tag available
  const needsAllTag = () => {
    try {
      return !normalizedTags.some(tag => tag && tag.code === 'all');
    } catch (err) {
      console.error("Error checking for 'all' tag:", err);
      return true;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">{t('fileTags.title')}</h3>
        {!isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('fileTags.addTag')}
          </button>
        )}
      </div>

      {needsAllTag() && (
        <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200 text-sm text-yellow-800">
          {t('fileTags.missingAllTag')}
        </div>
      )}

      <div className="bg-white rounded-md shadow-sm">
        {/* Tags List */}
        <ul className="divide-y divide-gray-200">
          {normalizedTags.map((tag, index) => (
            <li key={tag.code || index} className="p-4">
              {editingIndex === index ? (
                // Edit Mode
                <div className="flex flex-col space-y-2">
                  {/* Edit Code */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700">{t('fileTags.tagCode')}</label>
                    <input
                      type="text"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        errors[`code-${index}`] ? 'border-red-500' : ''
                      }`}
                      value={editedTag?.code || ''}
                      onChange={(e) => handleChange(index, 'code', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={(tag?.code === 'all')} // Only 'all' code is undeletable
                      ref={el => inputRefs.current['code-' + index] = el}
                    />
                    {errors[`code-${index}`] && (
                      <p className="mt-1 text-xs text-red-500">{errors[`code-${index}`]}</p>
                    )}
                  </div>
                  
                  {/* Language Tabs */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-4">
                      <button
                        type="button"
                        className={`${
                          editLanguage === 'en'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                        onClick={() => setEditLanguage('en')}
                      >
                        <Globe className="h-4 w-4 mr-1" />
                        EN
                      </button>
                      <button
                        type="button"
                        className={`${
                          editLanguage === 'cs'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                        onClick={() => setEditLanguage('cs')}
                      >
                        <Globe className="h-4 w-4 mr-1" />
                        CS
                      </button>
                    </nav>
                  </div>
                  
                  {/* Edit Labels */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      {editLanguage === 'en' 
                        ? t('fileTags.englishLabel') 
                        : t('fileTags.czechLabel')}
                    </label>
                    <input
                      type="text"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        errors[`label-${editLanguage}-${index}`] ? 'border-red-500' : ''
                      }`}
                      value={editedTag?.labels?.[editLanguage] || ''}
                      onChange={(e) => handleChange(index, 'labels', e.target.value, editLanguage)}
                      onFocus={(e) => e.target.select()}
                      ref={el => inputRefs.current[`${editLanguage}-${index}`] = el}
                    />
                    {errors[`label-${editLanguage}-${index}`] && (
                      <p className="mt-1 text-xs text-red-500">{errors[`label-${editLanguage}-${index}`]}</p>
                    )}
                  </div>
                  
                  {/* Edit Actions */}
                  <div className="flex justify-end space-x-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setEditingIndex(null)}
                      className="inline-flex items-center px-3 py-1 border rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {t('common.actions.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(index)}
                      className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {t('common.actions.save')}
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex items-center h-5">
                      <input
                        id={`tag-${tag.code}`}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={tag.enabled}
                        onChange={() => handleToggleTag(index)}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor={`tag-${tag.code}`} className="font-medium text-gray-700">
                        {getDisplayLabel(tag)}
                      </label>
                      <p className="text-gray-500">{tag.code}</p>
                      
                      {/* Show multi-language labels if available */}
                      {tag.labels && (
                        <div className="flex mt-1 space-x-2 text-xs text-gray-500">
                          {tag.labels.en && (
                            <span className="flex items-center">
                              <span className="font-medium mr-1">EN:</span> {tag.labels.en}
                            </span>
                          )}
                          {tag.labels.cs && (
                            <span className="flex items-center">
                              <span className="font-medium mr-1">CS:</span> {tag.labels.cs}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditTag(index)}
                      className="text-gray-400 hover:text-gray-500"
                      title={t('common.actions.edit')}
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteTag(index)}
                      className="text-red-400 hover:text-red-500"
                      disabled={tag.code === 'all'} // Only 'all' tag is undeletable
                      title={tag.code === 'all' 
                        ? t('fileTags.cannotDelete') 
                        : t('common.actions.delete')}
                    >
                      <Trash2 className={`h-5 w-5 ${tag.code === 'all' ? 'opacity-30' : ''}`} />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
          
          {/* Add New Tag Form */}
          {isAdding && (
            <li className="p-4 bg-blue-50">
              <div className="flex flex-col space-y-2">
                {/* Tag Code */}
                <div>
                  <label className="block text-xs font-medium text-gray-700">{t('fileTags.tagCode')}</label>
                  <input
                    type="text"
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors['new-code'] ? 'border-red-500' : ''
                    }`}
                    value={newTag.code}
                    onChange={(e) => handleNewTagChange('code', e.target.value)}
                    placeholder="e.g. invoice"
                  />
                  {errors['new-code'] && (
                    <p className="mt-1 text-xs text-red-500">{errors['new-code']}</p>
                  )}
                </div>
                
                {/* Language Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-4">
                    <button
                      type="button"
                      className={`${
                        editLanguage === 'en'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                      onClick={() => setEditLanguage('en')}
                    >
                      <Globe className="h-4 w-4 mr-1" />
                      EN
                    </button>
                    <button
                      type="button"
                      className={`${
                        editLanguage === 'cs'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                      onClick={() => setEditLanguage('cs')}
                    >
                      <Globe className="h-4 w-4 mr-1" />
                      CS
                    </button>
                  </nav>
                </div>
                
                {/* Display Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {editLanguage === 'en' 
                      ? t('fileTags.englishLabel') 
                      : t('fileTags.czechLabel')}
                  </label>
                  <input
                    type="text"
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors[`new-label-${editLanguage}`] ? 'border-red-500' : ''
                    }`}
                    value={newTag.labels[editLanguage]}
                    onChange={(e) => handleNewTagChange('labels', e.target.value, editLanguage)}
                    placeholder={editLanguage === 'en' ? 'e.g. Invoices' : 'např. Faktury'}
                  />
                  {errors[`new-label-${editLanguage}`] && (
                    <p className="mt-1 text-xs text-red-500">{errors[`new-label-${editLanguage}`]}</p>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAdding(false);
                      setNewTag({ ...DEFAULT_TAG });
                      setErrors({});
                    }}
                    className="inline-flex items-center px-3 py-1 border rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {t('common.actions.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('common.actions.create')}
                  </button>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
      
      <p className="text-xs text-gray-500">
        {t('fileTags.note')}
      </p>
    </div>
  );
};

export default FileTagsManagement;